import _extends from "@babel/runtime/helpers/extends";
import { Spin, Form, Button, Divider, Result, Row, Col, Alert, App } from "antd";
import { observer } from "mobx-react";
import { useMemo } from "react";
import { useEffect } from "react";
import { useState, useCallback } from "react";
import { IMPREZA_BUTTON_PROPS, _i, __ } from "../../../../utils";
import { RealCommerceError } from "../../../../exceptions";
import { useRealCommerceErrorHandling } from "../../../../hooks";
import { EAffiliateStatus, EAffiliateLanguage } from "@devowl-wp/api-real-commerce";
import { useStores } from "../../../../store";
import { AccountAffiliateTerms, AccountAffiliateSignUpGeneralFields, AccountAffiliateSignUpInvoiceAddressFields, AccountAffiliateSignUpPaymentDetailsFields, AccountAffiliateSignUpSidebar } from ".";
import { Affiliate } from "../../../../models";
const AccountAffiliateSignUpFormLayout = {
  labelCol: {
    span: 10
  },
  wrapperCol: {
    span: 14
  }
};

/**
 * Check if invoice address got changed and only send to backend when changed
 * as our backends do currently not support json-diff and json-path.
 *
 * Also, antd supports the `onChange` handler but this leads to false-positives when
 * e.g. the user removes one char and adds the same char to the input again (input value stays
 * the same).
 */
const AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS = ["companyName", "country", "firstName", "lastName", "street", "zip", "city", "companyIdentificationNumber", "taxNumber", "isSmallBusinessWithoutVatDeclaration", "vatId"];
const AccountAffiliateSignUp = observer(() => {
  const {
    message
  } = App.useApp();
  const [busy, setBusy] = useState(false);
  const [isEditMode, setEditMode] = useState(false);
  const [result, setResult] = useState(undefined);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const inviteUuid = params.get("invite") || undefined;
  const [invite, setInvite] = useState();

  // Determine default language of support
  const {
    lang
  } = document.documentElement;
  const communicationLanguage = lang.startsWith("de") ? EAffiliateLanguage.German : EAffiliateLanguage.English;
  const initialValues = {
    affiliate: {
      invoiceAddress: {},
      paymentDetails: {},
      communicationLanguage
    },
    user: {},
    interestedInProducts: [],
    terms: false,
    privacy: false,
    feeAgreement: false
  };
  const [form] = Form.useForm();
  const handleRealCommerceError = useRealCommerceErrorHandling();
  const {
    accountStore
  } = useStores();
  const {
    affiliateManufacturer,
    session,
    isLoggedIn,
    isAffiliate
  } = accountStore;
  if (isAffiliate) {
    const {
      affiliate: {
        affiliate: {
          payoutThreshold,
          communicationLanguage,
          invoiceAddress: {
            companyName,
            country,
            firstName,
            lastName,
            street,
            zip,
            city,
            companyIdentificationNumber,
            taxNumber,
            isSmallBusinessWithoutVatDeclaration,
            // tradeRegisterExcerpt, TODO
            vatId
          },
          paymentDetails: {
            accountHolder,
            currency,
            achAccountNumber,
            achRoutingNumber,
            iban,
            ukAccountNumber,
            ukSortCode
            // It is always truncated at this state
            //isTruncate
          }
        }
      }
    } = session;

    // Copy all mutable fields
    const {
      affiliate
    } = initialValues;
    const {
      invoiceAddress,
      paymentDetails
    } = affiliate;
    affiliate.communicationLanguage = communicationLanguage;
    affiliate.payoutThreshold = payoutThreshold;
    invoiceAddress.companyName = companyName;
    invoiceAddress.country = country;
    invoiceAddress.firstName = firstName;
    invoiceAddress.lastName = lastName;
    invoiceAddress.street = street;
    invoiceAddress.zip = zip;
    invoiceAddress.city = city;
    invoiceAddress.companyIdentificationNumber = companyIdentificationNumber;
    invoiceAddress.taxNumber = taxNumber;
    invoiceAddress.isSmallBusinessWithoutVatDeclaration = isSmallBusinessWithoutVatDeclaration;
    invoiceAddress.vatId = vatId;
    paymentDetails.accountHolder = accountHolder;
    paymentDetails.currency = currency;
    paymentDetails.achAccountNumber = achAccountNumber;
    paymentDetails.achRoutingNumber = achRoutingNumber;
    paymentDetails.iban = iban;
    paymentDetails.ukAccountNumber = ukAccountNumber;
    paymentDetails.ukSortCode = ukSortCode;
    paymentDetails.isTruncate = true;
    initialValues.user.email = session.user.email;
  } else if (isLoggedIn) {
    // User is logged in, lets prefill known data like first name
    const {
      invoiceAddress
    } = initialValues.affiliate;
    const {
      user: {
        firstName,
        lastName,
        email
      }
    } = session;
    invoiceAddress.firstName = firstName;
    invoiceAddress.lastName = lastName;
    initialValues.user.email = email;
  }

  // Automatically fill initial values from invite-data
  useEffect(() => {
    if (!isLoggedIn && typeof inviteUuid === "string") {
      setBusy(true);
      Affiliate.loadInvite(inviteUuid).then(invite => {
        const {
          invoiceAddress,
          communicationLanguage,
          email
        } = invite;
        setInvite(invite);
        form.setFieldsValue({
          affiliate: {
            communicationLanguage,
            invoiceAddress
          },
          user: {
            email
          }
        });
      }).finally(() => setBusy(false));
    }
  }, [inviteUuid]);

  // We need to store the pdf file as File instance as we can not get it through the form
  const [tradeRegisterExcerptFile, setTradeRegisterExcerptFile] = useState();
  const onFinish = useCallback(async _ref => {
    let {
      affiliate,
      user,
      interestedInProducts
    } = _ref;
    const {
      invoiceAddress
    } = affiliate;

    // Special case: e.g. Switzerland has the same UID as the tax number
    if (!invoiceAddress.taxNumber) {
      invoiceAddress.taxNumber = invoiceAddress.companyIdentificationNumber;
    }
    setBusy(true);
    try {
      if (isAffiliate) {
        // Check if invoice address got changed
        const invoiceAddressGotChanged = !!tradeRegisterExcerptFile || JSON.stringify(AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map(k => invoiceAddress[k])) !== JSON.stringify(AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS.map(k => session.affiliate.affiliate.invoiceAddress[k]));

        // Edit form
        await session.affiliate.updateDetails({
          ...affiliate,
          invoiceAddress: invoiceAddressGotChanged ? {
            ...invoiceAddress,
            tradeRegisterExcerpt: invoiceAddress.tradeRegisterExcerpt ? tradeRegisterExcerptFile : undefined
          } : undefined,
          // Only send this fields when they got changed
          paymentDetails: affiliate.paymentDetails.isTruncate ? undefined : affiliate.paymentDetails
        });
        message.success(__("Successfully updated the invoice & payment details!"));
        setEditMode(false);
      } else {
        const {
          affiliate: result
        } = await accountStore.createAffiliate({
          ...affiliate,
          interestedInProducts: interestedInProducts.map(id => ({
            id
          })),
          invoiceAddress: {
            ...invoiceAddress,
            tradeRegisterExcerpt: invoiceAddress.tradeRegisterExcerpt ? tradeRegisterExcerptFile : undefined
          }
        }, {
          ...user,
          firstName: invoiceAddress.firstName,
          lastName: invoiceAddress.lastName
        }, invite === null || invite === void 0 ? void 0 : invite.uuid);
        setResult(result);
      }
    } catch (e) {
      if (e instanceof RealCommerceError) {
        handleRealCommerceError(e);
      }
    } finally {
      setBusy(false);
    }
  }, [tradeRegisterExcerptFile, accountStore, session, invite]);
  return result ? /*#__PURE__*/React.createElement(Result, {
    status: "success",
    title: __("Thanks for signing up as affiliate, %s!", result.user.firstName),
    subTitle: result.status === EAffiliateStatus.Active ? __("You have successfully signed up to our affiliate program. Please check your inbox to activate the account!") : __("You have successfully signed up to our affiliate program. Please check your inbox to activate your user account! In addition, your affiliate request will be checked separately. We will contact you by email! Normally this takes about one business day.")
  }) : /*#__PURE__*/React.createElement("div", {
    className: "real-commerce-create-account-form",
    style: {
      marginTop: isAffiliate ? 25 : 0
    }
  }, /*#__PURE__*/React.createElement(Spin, {
    spinning: busy
  }, /*#__PURE__*/React.createElement(Row, null, !isAffiliate && /*#__PURE__*/React.createElement(Col, {
    md: 10
  }, /*#__PURE__*/React.createElement(AccountAffiliateSignUpSidebar, {
    invite: invite
  })), /*#__PURE__*/React.createElement(Col, {
    md: isAffiliate ? 24 : 14
  }, isAffiliate && /*#__PURE__*/React.createElement(Alert, {
    type: "info",
    style: {
      marginBottom: 20
    },
    message: _i(__("The following information applies to your affiliate contract. Changes in this section {{strong}}do not{{/strong}} change the contract data and payment data of your subscriptions."), {
      strong: /*#__PURE__*/React.createElement("strong", null)
    })
  }), /*#__PURE__*/React.createElement(Form, _extends({
    form: form,
    onFinish: onFinish,
    initialValues: initialValues,
    colon: false,
    style: {
      paddingLeft: 25
    },
    labelWrap: true
  }, AccountAffiliateSignUpFormLayout), !isAffiliate && /*#__PURE__*/React.createElement(Divider, {
    orientation: "left"
  }, __("General")), /*#__PURE__*/React.createElement(AccountAffiliateSignUpGeneralFields, null), /*#__PURE__*/React.createElement(AccountAffiliateSignUpInvoiceAddressFields, {
    setTradeRegisterExcerptFile: setTradeRegisterExcerptFile
  }), /*#__PURE__*/React.createElement(AccountAffiliateSignUpPaymentDetailsFields, {
    form: form,
    toggleEdit: [isEditMode, setEditMode]
  }), !isAffiliate && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Divider, {
    orientation: "left"
  }, __("Consents")), /*#__PURE__*/React.createElement(AccountAffiliateTerms, {
    manufacturer: affiliateManufacturer,
    formItemAttr: AccountAffiliateSignUpFormLayout
  })), /*#__PURE__*/React.createElement(Form.Item, {
    wrapperCol: {
      offset: AccountAffiliateSignUpFormLayout.labelCol.span
    }
  }, /*#__PURE__*/React.createElement(Button, _extends({
    type: "primary",
    size: "large",
    htmlType: "submit"
  }, IMPREZA_BUTTON_PROPS), isAffiliate ? __("Save") : __("Sign up as affiliate"))))))));
});
export { AccountAffiliateSignUp, AccountAffiliateSignUpFormLayout, AFFILIATE_INVOICE_ADDRESS_CHANGE_FIELDS };